import { useMutation, useQuery } from '@apollo/client';
import { useFeatureFlag } from '@newfront-insurance/admin-ui';
import { useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { NovaScheduleCreateView, type ManualNovaScheduleCreateInput } from './nova-schedule-create-view';
import { NovaSchedulesList } from './nova-schedules-list';
import { NOVA_SCHEDULES_FEATURE_FLAG } from '../../../../lib/nova-schedule-proposal';
import { GET_NOVA_SCHEDULES, isNovaScheduleRecord } from '../../../../queries/nova-schedule-management';
import { CREATE_NOVA_SCHEDULE, type CreateNovaScheduleCommand } from '../../../../queries/nova-schedule-mutations';
import { useChatOverlayContext } from '../../contexts/chat-overlay-context';
import { normalizeChatUuid } from '../../lib/notification-helpers';
import { useSettingsPanelContext } from '../settings-panel-context';

const MANAGEABLE_SCHEDULE_STATUSES = ['ACTIVE', 'PAUSED', 'COMPLETED'] as const;

export function NovaSchedulesSettings() {
  const schedulesEnabled = useFeatureFlag(NOVA_SCHEDULES_FEATURE_FLAG, false);
  const { chatUuid, userScopes, userUuid, setIsSettingsOpen, startNewChatWithInput } = useChatOverlayContext();
  const { pushView } = useSettingsPanelContext();
  const [isCreating, setIsCreating] = useState(false);
  const [createSchedule, createState] = useMutation(CREATE_NOVA_SCHEDULE);
  const { data, loading, error, refetch } = useQuery(GET_NOVA_SCHEDULES, {
    variables: {
      filters: {
        ownerUserUuids: [userUuid],
        statuses: [...MANAGEABLE_SCHEDULE_STATUSES],
      },
    },
    skip: !schedulesEnabled || !userUuid,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  if (!schedulesEnabled) return null;

  const schedules = data?.getNovaSchedules.filter(isNovaScheduleRecord) ?? [];
  const showCreateView = isCreating || (!loading && !error && schedules.length === 0);

  const createManualSchedule = async (input: ManualNovaScheduleCreateInput) => {
    const scheduleUuid = uuidv4();
    const command: CreateNovaScheduleCommand = {
      uuid: scheduleUuid,
      ownerUserUuid: userUuid,
      sourceChatUuid: normalizeChatUuid(chatUuid),
      ...(input.account?.uuid ? { accountUuid: input.account.uuid } : {}),
      title: input.title,
      prompt: input.prompt,
      ...(input.attachmentFileUuids.length ? { attachmentFileUuids: input.attachmentFileUuids } : {}),
      timezone: input.timezone,
      recurrence: input.recurrence,
      ...(input.startAt ? { startAt: input.startAt } : {}),
      ...(input.outputPolicy ? { outputPolicy: input.outputPolicy } : {}),
    };

    try {
      const result = await createSchedule({ variables: { command } });
      if (result.data?.createNovaSchedule.uuid !== scheduleUuid) {
        throw new Error('The create mutation did not return the requested schedule.');
      }
      await refetch();
      setIsCreating(false);
      pushView({ type: 'nova-schedule-detail', scheduleUuid });
    } catch {
      // Apollo exposes the error through createState while the form stays open.
    }
  };

  if (showCreateView) {
    return (
      <NovaScheduleCreateView
        showBack={schedules.length > 0}
        loggedInUserUuid={userUuid}
        userScopes={userScopes ?? []}
        isSaving={createState.loading}
        error={createState.error}
        onBack={() => setIsCreating(false)}
        onPromptSubmit={(prompt, attachmentFileUuids) => {
          setIsSettingsOpen(false);
          startNewChatWithInput(prompt, {
            additionalContext: { attachmentFileUuids },
            submitImmediately: true,
          });
        }}
        onManualSubmit={createManualSchedule}
      />
    );
  }

  return (
    <NovaSchedulesList
      schedules={schedules}
      isLoading={loading && data === undefined}
      error={error}
      onNewSchedule={() => setIsCreating(true)}
      onRetry={() => void refetch()}
      onSelectSchedule={(scheduleUuid) => pushView({ type: 'nova-schedule-detail', scheduleUuid })}
    />
  );
}
